import type { ReactNode } from "react"

import {
  useCustomerApplicationsLatestSubmittedRetrieve,
  useCustomerCompanyList,
} from "@boundless/bl-api-client/customer/services"
import { usePublicBoundlessConfigRetrieveSuspense } from "@boundless/bl-api-client/public/services"
import { linkOptions, type LinkOptions } from "@tanstack/react-router"
import {
  ArrowLeftRightIcon,
  BookTextIcon,
  BuildingIcon,
  ChartPieIcon,
  CircleUserIcon,
  ContactIcon,
  FileIcon,
  FilePenLineIcon,
  HandCoinsIcon,
  HandshakeIcon,
  HeadsetIcon,
  HomeIcon,
  LandmarkIcon,
  LockIcon,
  type LucideIcon,
  SettingsIcon,
  StarIcon,
  UserIcon,
  UserRoundIcon,
  UsersIcon,
} from "lucide-react"
import { useStore } from "zustand"

import { companyListQueryOptions, isCurrentUserOwner } from "@/lib/companies"
import { store } from "@/lib/store"

type DashboardNavBaseItem = {
  id: string
  name: string
  icon: LucideIcon
  rightIcon?: LucideIcon
  disabled?: boolean
  tooltip?: ReactNode
  mobilePrimary?: boolean
  mobileLabel?: string
}

export type DashboardInternalNavItem = DashboardNavBaseItem & {
  kind: "internal"
  navLinkOptions: LinkOptions
}

export type DashboardExternalNavItem = DashboardNavBaseItem & {
  kind: "external"
  href: string
  newTab?: boolean
}

export type DashboardNavItem =
  | DashboardInternalNavItem
  | DashboardExternalNavItem

export type DashboardNavigation = {
  topItems: DashboardNavItem[]
  bottomItems: DashboardNavItem[]
}

export function isInternalNavItem(
  item: DashboardNavItem,
): item is DashboardInternalNavItem {
  return item.kind === "internal"
}

function internalItem({
  navLinkOptions,
  ...item
}: Omit<DashboardInternalNavItem, "kind">): DashboardInternalNavItem {
  return {
    ...item,
    kind: "internal",
    navLinkOptions,
  }
}

function externalItem({
  href,
  newTab = true,
  ...item
}: Omit<DashboardExternalNavItem, "kind">): DashboardExternalNavItem {
  return {
    ...item,
    kind: "external",
    href,
    newTab,
  }
}

export function useDashboardNavigation(): DashboardNavigation {
  const userRole = useStore(store, (state) => state.userRole)
  const activeCompanyUuid = useStore(store, (state) => state.activeCompanyUuid)
  const perksEnabled = useStore(store, (state) => state.perksEnabled)
  const hasPermissions = useStore(store, (state) => state.hasPermissions)

  const { data: boundlessConfig } = usePublicBoundlessConfigRetrieveSuspense()

  // Read through the hook rather than the store's `hasPermissions`: the Users
  // item depends on the active company's Company Role, which arrives with the
  // company list and has to re-render this nav when it does.
  const { data: companies } = useCustomerCompanyList(
    {},
    companyListQueryOptions(userRole === "customer"),
  )

  const { data: application } = useCustomerApplicationsLatestSubmittedRetrieve(
    activeCompanyUuid ?? "",
    {
      query: {
        enabled: userRole === "customer" && !!activeCompanyUuid,
      },
    },
  )

  if (userRole === "partner") {
    return {
      topItems: [
        internalItem({
          id: "home",
          name: "Home",
          mobileLabel: "Home",
          mobilePrimary: true,
          icon: HomeIcon,
          navLinkOptions: linkOptions({ to: "/partners/home" }),
        }),
        internalItem({
          id: "referrals",
          name: "Referrals",
          mobileLabel: "Refs",
          mobilePrimary: true,
          icon: HandCoinsIcon,
          navLinkOptions: linkOptions({ to: "/partners/referrals" }),
        }),
        internalItem({
          id: "banking",
          name: "Banking Details",
          mobileLabel: "Bank",
          mobilePrimary: true,
          icon: LandmarkIcon,
          navLinkOptions: linkOptions({ to: "/partners/banking" }),
        }),
        internalItem({
          id: "guide",
          name: "Partner Guide",
          mobileLabel: "Guide",
          mobilePrimary: true,
          icon: BookTextIcon,
          navLinkOptions: linkOptions({ to: "/partners/guide" }),
        }),
        externalItem({
          id: "contact",
          name: "Contact",
          icon: ContactIcon,
          href: boundlessConfig.supportUrlPartner,
        }),
      ],
      bottomItems: [
        internalItem({
          id: "companies",
          name: "Companies",
          icon: BuildingIcon,
          navLinkOptions: linkOptions({ to: "/partners/companies" }),
          tooltip: (
            <div className="overflow-hidden text-center text-nowrap">
              <p className="font-medium">Multiple corporations?</p>
              <p>
                Add them all to your account and easily toggle between them to
                <br />
                view and manage their applications seamlessly.
              </p>
            </div>
          ),
        }),
        internalItem({
          id: "users",
          name: "Manage Users",
          icon: UserIcon,
          navLinkOptions: linkOptions({ to: "/partners/users" }),
        }),
        internalItem({
          id: "settings",
          name: "Settings",
          icon: SettingsIcon,
          navLinkOptions: linkOptions({ to: "/settings/my-account" }),
        }),
      ],
    }
  }

  if (userRole === "lender") {
    const hasReferralsViewPermission = hasPermissions([
      "customer.view_company",
      "partners.view_partner",
    ])

    return {
      topItems: [
        internalItem({
          id: "home",
          name: "Home",
          mobileLabel: "Home",
          mobilePrimary: true,
          icon: HomeIcon,
          navLinkOptions: linkOptions({ to: "/lender/home" }),
        }),
        internalItem({
          id: "referrals",
          name: "Referrals",
          mobileLabel: "Refs",
          mobilePrimary: true,
          icon: HandCoinsIcon,
          rightIcon: hasReferralsViewPermission ? undefined : LockIcon,
          disabled: !hasReferralsViewPermission,
          navLinkOptions: linkOptions({ to: "/partners/referrals" }),
        }),
        internalItem({
          id: "applications",
          name: "Applications",
          mobileLabel: "Apps",
          mobilePrimary: true,
          icon: FilePenLineIcon,
          navLinkOptions: linkOptions({ to: "/lender/applications" }),
        }),
        internalItem({
          id: "profile",
          name: "Lender Profile",
          mobileLabel: "Profile",
          mobilePrimary: true,
          icon: CircleUserIcon,
          navLinkOptions: linkOptions({ to: "/lender/profile" }),
        }),
        externalItem({
          id: "contact",
          name: "Contact",
          icon: HeadsetIcon,
          href: boundlessConfig.supportUrlLender,
        }),
      ],
      bottomItems: [
        internalItem({
          id: "users",
          name: "Manage Users",
          icon: UserIcon,
          navLinkOptions: linkOptions({ to: "/lender/users" }),
        }),
        internalItem({
          id: "settings",
          name: "Settings",
          icon: SettingsIcon,
          navLinkOptions: linkOptions({ to: "/settings/my-account" }),
        }),
      ],
    }
  }

  if (userRole === "customer") {
    const myBusinessProfileItem = application?.uuid
      ? internalItem({
          id: "business-profile",
          name: "My Business Profile",
          mobileLabel: "Business",
          mobilePrimary: true,
          icon: UserRoundIcon,
          navLinkOptions: linkOptions({
            to: "/applications/$applicationUuid/summary",
            params: { applicationUuid: application.uuid },
          }),
        })
      : internalItem({
          id: "business-profile",
          name: "My Business Profile",
          mobileLabel: "Business",
          mobilePrimary: true,
          icon: UserRoundIcon,
          navLinkOptions: linkOptions({ to: "/company" }),
        })

    const bottomItems: DashboardNavItem[] = []

    if (perksEnabled) {
      bottomItems.push(
        internalItem({
          id: "perks",
          name: "Perks",
          icon: StarIcon,
          navLinkOptions: linkOptions({ to: "/perks" }),
        }),
      )
    }

    bottomItems.push(
      internalItem({
        id: "companies",
        name: "Companies",
        icon: ArrowLeftRightIcon,
        navLinkOptions: linkOptions({ to: "/companies" }),
      }),
    )

    // Managing members and invitations is Owner-only, and the page redirects
    // a Collaborator straight back out, so it is not offered at all.
    const ownsActiveCompany = isCurrentUserOwner(
      companies?.find((c) => c.uuid === activeCompanyUuid),
    )

    if (ownsActiveCompany) {
      bottomItems.push(
        internalItem({
          id: "users",
          name: "Users",
          icon: UsersIcon,
          navLinkOptions: linkOptions({ to: "/users" }),
        }),
      )
    }

    bottomItems.push(
      internalItem({
        id: "refer-friend",
        name: "Refer a Friend",
        icon: HandshakeIcon,
        navLinkOptions: linkOptions({ to: "/refer" }),
      }),
      internalItem({
        id: "settings",
        name: "Settings",
        icon: SettingsIcon,
        navLinkOptions: linkOptions({ to: "/settings/my-account" }),
      }),
    )

    return {
      topItems: [
        internalItem({
          id: "home",
          name: "Home",
          mobileLabel: "Home",
          mobilePrimary: true,
          icon: HomeIcon,
          navLinkOptions: linkOptions({ to: "/home" }),
        }),
        myBusinessProfileItem,
        internalItem({
          id: "funding-requests",
          name: "Funding Requests",
          mobileLabel: "Funding",
          mobilePrimary: true,
          icon: HandCoinsIcon,
          navLinkOptions: linkOptions({ to: "/funding-requests" }),
          disabled: !activeCompanyUuid,
        }),
        internalItem({
          id: "documents",
          name: "Documents",
          mobileLabel: "Documents",
          mobilePrimary: true,
          icon: FileIcon,
          navLinkOptions: linkOptions({ to: "/documents" }),
          disabled: !activeCompanyUuid,
        }),
        internalItem({
          id: "debt-table",
          name: "Debt Table",
          mobileLabel: "Debt Table",
          mobilePrimary: true,
          icon: ChartPieIcon,
          navLinkOptions: linkOptions({ to: "/debt-table" }),
          disabled: !activeCompanyUuid,
        }),
        externalItem({
          id: "contact",
          name: "Contact",
          icon: HeadsetIcon,
          href: boundlessConfig.supportUrlCustomer,
        }),
      ],
      bottomItems,
    }
  }

  return {
    topItems: [],
    bottomItems: [],
  }
}
